/**
 * The reads of an item's standing relative to a cycle (migration 1019).
 *
 *   fetchItemCycleStanding  the item page: its cycles, their rank modes, and the
 *                           plans that hold it with what each holds
 *   fetchHeldValues         a plan's readers: what a plan holds for these items,
 *                           title and description included (migration 1060).
 *                           An item no plan holds has no entry: read it live.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { CycleRankMode, HeldCategory, ItemCycleStanding, ItemHeldValues, ItemPlanHold } from './itemCycleStanding';

interface RawHeld {
  title?: string | null;
  description?: string | null;
  item_category?: string | null;
  altitude_level?: number | null;
  effort_capacity_units?: number | string | null;
  work_type?: string | null;
  launch_type?: string | null;
  investment_type?: string | null;
  additional_categories?: unknown;
}

const toHeld = (r: RawHeld): ItemHeldValues => ({
  title: r.title ?? null,
  description: r.description ?? null,
  itemCategory: r.item_category ?? null,
  altitudeLevel: r.altitude_level ?? null,
  effortCapacityUnits: r.effort_capacity_units != null ? Number(r.effort_capacity_units) : null,
  workType: r.work_type ?? null,
  launchType: r.launch_type ?? null,
  investmentType: r.investment_type ?? null,
  additionalCategories: Array.isArray(r.additional_categories) ? (r.additional_categories as HeldCategory[]) : [],
});

interface RawStanding {
  cycle: { id: string; name: string; mode: CycleRankMode; in_cycle: boolean } | null;
  cycles: Array<{ id: string; name: string; mode: CycleRankMode }>;
  plans: Array<{
    plan_cycle_id: string;
    plan_name: string;
    cycle_id: string | null;
    cycle_name: string | null;
    state: ItemPlanHold['state'];
    rank: number | null;
    above_the_line: boolean | null;
    authorized_at: string | null;
    version: number | null;
    latest_version: number | null;
    may_amend?: boolean;
    held: RawHeld;
  }>;
  latest_version: number | null;
}

/** Null when it could not be read: the page then keeps its plan-in-force rule. */
export async function fetchItemCycleStanding(itemId: string, cycleId: string | null): Promise<ItemCycleStanding | null> {
  const { data, error } = await getTypedSupabaseClient().rpc('get_item_cycle_standing', {
    p_item_id: itemId,
    ...(cycleId ? { p_cycle_id: cycleId } : {}),
  });
  if (error || !data) return null;
  const d = data as unknown as RawStanding;
  return {
    cycle: d.cycle ? { id: d.cycle.id, name: d.cycle.name, mode: d.cycle.mode, inCycle: d.cycle.in_cycle } : null,
    cycles: d.cycles ?? [],
    plans: (d.plans ?? []).map((p) => ({
      planCycleId: p.plan_cycle_id,
      planName: p.plan_name,
      cycleId: p.cycle_id,
      cycleName: p.cycle_name,
      state: p.state,
      rank: p.rank,
      aboveTheLine: p.above_the_line,
      authorizedAt: p.authorized_at,
      version: p.version,
      latestVersion: p.latest_version,
      mayAmend: p.may_amend === true,
      held: toHeld(p.held ?? {}),
    })),
    latestVersion: d.latest_version ?? null,
  };
}

/**
 * What a plan holds for these items: the named plan, or with none the most
 * recently authorized plan in force. A reader that fails here must not show the
 * live item as the plan's, so the caller is told: null means it could not be read.
 */
export async function fetchHeldValues(itemIds: readonly string[], planCycleId?: string | null): Promise<Map<string, ItemHeldValues> | null> {
  const ids = [...new Set(itemIds.filter(Boolean))];
  if (ids.length === 0) return new Map();
  const { data, error } = await getTypedSupabaseClient().rpc('item_held_values', {
    p_item_ids: ids,
    ...(planCycleId ? { p_plan_cycle_id: planCycleId } : {}),
  });
  if (error || !data) return null;
  return new Map((data as Array<RawHeld & { item_id: string }>).map((r) => [r.item_id, toHeld(r)]));
}
